import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Loader from "../Common/Loader";
import { getBranchesAction } from "../../../store/actions/BranchAction";
import { getStockTransactionsAction } from "../../../store/actions/stockTransactionActions";

const StockTransactions = () => {
  const dispatch = useDispatch();

  const branches = useSelector((state) => state.branches?.branches || []);
  const branchesLoading = useSelector((state) => state.branches?.loading || false);
  
  // Get transactions from Redux store
  const { transactions = [], num_rows = 0, loading: transactionsLoading = false, error } = useSelector(
    (state) => state.stockTransaction || {}
  );

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  // Fetch branches on load and set default
  useEffect(() => {
    dispatch(getBranchesAction(false));
  }, [dispatch]);

  // Set default branch to first one when branches load
  useEffect(() => {
    if (branches && branches.length > 0 && !selectedBranch) {
      const firstBranch = branches[0];
      setSelectedBranch({
        value: firstBranch.branch_id,
        label: firstBranch.branch_name,
      });
    }
  }, [branches, selectedBranch]);

  // Fetch transactions when page changes
  useEffect(() => {
    fetchTransactions();
  }, [currentPage]);

  const fetchTransactions = async () => {
    await dispatch(getStockTransactionsAction(
      true,           // pagination
      currentPage,    // pageid
      "",             // search_by
      "",             // search_keyword
      "date",         // sort_by
      "desc"          // sort_order
    ));
  };

  const branchOptions = branches.map((b) => ({
    value: b.branch_id,
    label: b.branch_name,
  }));

  const handleBranchChange = (selected) => {
    setSelectedBranch(selected);
    setCurrentPage(0);
  };

  const totalPages = Math.ceil(num_rows / rowsPerPage);
  const paginatedData = transactions;

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  const getTransactionBadge = (type) => {
    if (type?.toLowerCase() === "inward") {
      return <span className="badge bg-success">Inward</span>;
    } else if (type?.toLowerCase() === "outward") {
      return <span className="badge bg-warning text-dark">Outward</span>;
    }
    return <span className="badge bg-secondary">{type || "-"}</span>;
  };

  const isLoading = branchesLoading || transactionsLoading;

  return (
    <>
      <div className="row mb-3 align-items-end">
        <div className="col-lg-6 col-md-6">
          <h4 className="mb-0">
            <i className="fas fa-exchange-alt me-2"></i>
            Inward/Outward Stock transactions
          </h4>
        </div>
        <div className="col-lg-6 col-md-6 ">
          {branchesLoading ? (
            <div style={{ height: "45px" }}>
              <Loader size="sm" />
            </div>
          ) : (
            <div style={{ maxWidth: "300px", marginLeft: "auto" }}>
              <Select
                options={branchOptions}
                value={selectedBranch}
                onChange={handleBranchChange}
                placeholder="Select Branch"
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "45px",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="alert alert-danger mb-3">{error}</div>}
              
              {isLoading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Medicine Details</th>
                          <th>Transaction</th>
                          <th>Qty</th>
                          <th>Balance Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.length > 0 ? (
                          paginatedData.map((item) => (
                            <tr key={item.id}>
                              <td>{item.date ? item.date.split("-").reverse().join("-") : "-"}</td>
                              <td>
                                <div>
                                  <strong>{item.medicine_name}</strong>
                                  {item.batch_no && (
                                    <div className="small text-muted">Batch: {item.batch_no}</div>
                                  )}
                                  {item.carton_id && (
                                    <div className="small text-muted">Carton: {item.carton_id}</div>
                                  )}
                                </div>
                              </td>
                              <td>{getTransactionBadge(item.transaction_type)}</td>
                              <td className="fw-bold">{item.qty}</td>
                              <td className="fw-bold">{item.balance_qty}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-5">
                              <i className="fas fa-boxes fa-3x text-muted mb-3 d-block"></i>
                              <p className="text-muted">No transactions found</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {num_rows > 0 && totalPages > 1 && (
                    <div
                      className="pagination-wrapper"
                      style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "20px",
                        flexWrap: "wrap",
                        flexDirection: "column",
                      }}
                    >
                      <div>
                        Showing {currentPage * rowsPerPage + 1} to{" "}
                        {Math.min((currentPage + 1) * rowsPerPage, num_rows)} of {num_rows} entries
                      </div>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                        <button
                          onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                          disabled={currentPage === 0}
                          className="btn btn-sm btn-outline-secondary"
                        >
                          &lt;
                        </button>

                        {(() => {
                          const pages = [];
                          const maxPagesToShow = 5;
                          let startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow));
                          let endPage = Math.min(startPage + maxPagesToShow, totalPages);

                          if (startPage > 0) {
                            pages.push(
                              <button
                                key={0}
                                onClick={() => handlePageChange(0)}
                                className={`btn btn-sm ${currentPage === 0 ? "btn-primary" : "btn-outline-secondary"}`}
                              >
                                1
                              </button>
                            );
                            if (startPage > 1) {
                              pages.push(<span key="dots1" className="px-2">...</span>);
                            }
                          }

                          for (let i = startPage; i < endPage; i++) {
                            const isActive = currentPage === i;
                            pages.push(
                              <button
                                key={i}
                                onClick={() => handlePageChange(i)}
                                className={`btn btn-sm ${isActive ? "btn-primary" : "btn-outline-secondary"}`}
                              >
                                {i + 1}
                              </button>
                            );
                          }

                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(<span key="dots2" className="px-2">...</span>);
                            }
                            pages.push(
                              <button
                                key={totalPages - 1}
                                onClick={() => handlePageChange(totalPages - 1)}
                                className={`btn btn-sm ${currentPage === totalPages - 1 ? "btn-primary" : "btn-outline-secondary"}`}
                              >
                                {totalPages}
                              </button>
                            );
                          }

                          return pages;
                        })()}

                        <button
                          onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                          disabled={currentPage === totalPages - 1}
                          className="btn btn-sm btn-outline-secondary"
                        >
                          &gt;
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StockTransactions;